import type { DealTransformed } from "@/lib/schema/deal-schema";
import { tearsheetProgress } from "@/lib/tearsheet-stages";
import { FileText, Loader2, ScrollText, Table2 } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { aiApi } from "@/api/ai-api";
import { activateDeal, deepenDiligence, swapPremiumDeal } from "@/api/deals-api";
import type { GetReportStatusResponse } from "@/api/gen-api";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useDealStatsStore } from "@/store/deal-stats-store";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { getErrorMessage } from "@/lib/api-error";
import { VersionSelector, type VersionedItem } from "./version-selector";
import { ReportActionsMenu } from "./report-actions-menu";
import { AgentReportRows } from "./agent-report-rows";
import { Button } from "@/components/ui/button";
import { PremiumActivationDialog } from "@/components/modals/premium-activation-dialog";
import { PremiumSwapModal } from "@/components/modals/premium-swap-modal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ReportItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  isPremium: boolean;
  dataKey: string;
  /** report_status key when it differs from dataKey */
  statusKey?: string;
  /** Whether the report view gates behind currentDeal.aiTracked */
  requiresActivation: boolean;
  /**
   * Optional extra check beyond array-length — the report view may consider
   * entries empty/invalid even when the array has items.
   */
  isReportValid?: (entry: unknown) => boolean;
}

const reportItems: ReportItem[] = [
  {
    id: "tear-sheet",
    title: "Tear Sheet",
    description: "1-2 page executive summary snapshot",
    icon: FileText,
    isPremium: false,
    // Left-list version picker reads from the unified `tearsheets_all`
    // virtual key so legacy (flat `tearsheet`) and new (`data[]` versions)
    // docs both appear side-by-side in the dropdown.
    dataKey: "tearsheets_all",
    statusKey: "tearsheet",
    requiresActivation: false,
    // Each entry carries a `kind` discriminator; both shapes are valid.
    isReportValid: (entry) => {
      if (!entry || typeof entry !== "object") return false;
      const e = entry as { kind?: unknown };
      return e.kind === "legacy" || e.kind === "v2";
    },
  },
  {
    id: "deal-brief",
    title: "Deal Brief",
    description: "Deep diligence evaluation report",
    icon: ScrollText,
    isPremium: true,
    dataKey: "summaries",
    statusKey: "summary",
    requiresActivation: true,
  },
  {
    id: "metrics",
    title: "Metrics",
    description: "Company financial metrics and KPIs",
    icon: Table2,
    isPremium: true,
    dataKey: "metrics",
    requiresActivation: true,
    // Mirrors validMetrics in metrics.tsx: a report counts as "available"
    // only when it has at least one metric with a populated data array.
    isReportValid: (entry) => {
      const r = entry as { metrics?: Array<{ data?: unknown[] }> };
      return (
        Array.isArray(r?.metrics) &&
        r.metrics.length > 0 &&
        r.metrics.some((m) => Array.isArray(m?.data))
      );
    },
  },
];

interface ReportsTabProps {
  currentDeal: DealTransformed;
  reportStatus: unknown;
  onOpenInPanel?: (type: string, title: string, versionId?: string) => void;
  /** Open a finished agent report, which lives in the Agents tab. */
  onOpenAgentReport?: (templateId: string) => void;
}

function ReportsTab({
  currentDeal,
  reportStatus,
  onOpenInPanel,
  onOpenAgentReport,
}: ReportsTabProps) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { updateDeal } = useCurrentDealStore();
  const { dealStats, fetchDealStats } = useDealStatsStore();
  const { quota, orgId } = useOrgQuota();
  const queryClient = useQueryClient();

  const [selectedVersionByItem, setSelectedVersionByItem] = useState<
    Record<string, string>
  >({});
  const [pendingItems, setPendingItems] = useState<Set<string>>(new Set());
  const [isDeepening, setIsDeepening] = useState(false);

  // Takes a light-screened deal to full diligence (SAAS-915). The banner is
  // hidden optimistically on success; the reports themselves arrive from the
  // sequence worker over the following minutes, the same as on creation.
  const handleDeepen = async () => {
    if (!activeWorkspaceId) return;
    setIsDeepening(true);
    try {
      const response = await deepenDiligence(activeWorkspaceId, currentDeal.id);
      updateDeal({ diligenceLevel: "full" });
      toast.success(
        response.data.regenerated
          ? "Full diligence started — reports will update as they finish"
          : "This deal already had full diligence"
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't start full diligence"));
    } finally {
      setIsDeepening(false);
    }
  };
  const pendingOpenRef = useRef<Set<string>>(new Set());
  const genBaselineRef = useRef<Record<string, string | undefined>>({});
  const [deleteTarget, setDeleteTarget] = useState<
    | {
        item: ReportItem;
        versionId: string;
      }
    | null
  >(null);
  const [runConfirmItem, setRunConfirmItem] = useState<ReportItem | null>(null);
  const [swapItem, setSwapItem] = useState<ReportItem | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const markPending = (id: string) =>
    setPendingItems((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  const clearPending = (id: string) =>
    setPendingItems((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  /** Versions array (sorted newest-first) for a given report item. */
  const getVersions = (item: ReportItem): VersionedItem[] => {
    const gen = currentDeal.aiGenerations as Record<string, unknown>;
    const raw = gen?.[item.dataKey];
    if (!Array.isArray(raw)) return [];
    const valid = item.isReportValid
      ? raw.filter(item.isReportValid)
      : raw;
    return (valid as VersionedItem[])
      .filter((v) => v._id)
      .sort((a, b) => {
        const ad = a.created_on ? new Date(a.created_on).getTime() : 0;
        const bd = b.created_on ? new Date(b.created_on).getTime() : 0;
        return bd - ad;
      });
  };

  const hasData = (item: ReportItem): boolean => {
    const gen = currentDeal.aiGenerations as Record<string, unknown>;
    if (!gen) return false;
    const data = gen[item.dataKey];
    if (Array.isArray(data)) {
      if (data.length === 0) return false;
      if (item.isReportValid) return data.some(item.isReportValid);
      return true;
    }
    return !!data;
  };

  const getReportStatusValue = (item: ReportItem): string | undefined => {
    if (!reportStatus || typeof reportStatus !== "object") return undefined;
    const rs = reportStatus as Record<string, unknown>;
    const status = rs?.opportunityApplication as Record<string, unknown>;
    if (!status?.report_status) return undefined;
    const reports = status.report_status as Record<string, string>;
    return reports[item.statusKey ?? item.dataKey];
  };

  const isGenerating = (item: ReportItem): boolean => {
    const value = getReportStatusValue(item);
    return (
      value === "in_progress" ||
      value === "queued" ||
      value === "pending" ||
      (typeof value === "string" && value.startsWith("generating_"))
    );
  };

  const triggerGeneration = async (item: ReportItem): Promise<unknown> => {
    if (!activeWorkspaceId) throw new Error("No workspace selected");
    const params = {
      companyId: currentDeal.id,
      opportunityId: activeWorkspaceId,
      applicationInfo: currentDeal.dealInfo.applicationInfo,
      applicationObject: currentDeal.dealInfo.applicationInfo,
    };
    switch (item.id) {
      case "tear-sheet":
        return aiApi.startCustomizeUiTearsheet({
          companyId: currentDeal.id,
          opportunityId: activeWorkspaceId,
        });
      case "deal-brief":
        return aiApi.getDealBrief(params);
      case "metrics":
        return aiApi.getMetrics(params);
      default:
        throw new Error(`No generator for ${item.id}`);
    }
  };

  const generateMutation = useMutation({
    // Set the optimistic "pending" flag synchronously on click. If we waited
    // for onSuccess, react-query's isPending would flip to false in a
    // separate render cycle from our setPendingItems update, leaving a
    // one-frame window where showSpinner=false and the button flashes "Run".
    onMutate: (item: ReportItem) => {
      markPending(item.id);
    },
    mutationFn: async (item: ReportItem) => {
      if (!activeWorkspaceId) throw new Error("No workspace selected");

      const wasActivated = currentDeal.aiTracked === true;
      if (item.requiresActivation && !wasActivated) {
        const activation = await activateDeal(
          activeWorkspaceId,
          currentDeal.id
        );
        updateDeal({
          aiTracked: true,
          overview: {
            ...currentDeal.overview,
            status:
              activation.data.newStatus || currentDeal.overview.status,
          },
        });
        await fetchDealStats(activeWorkspaceId);
      }

      return triggerGeneration(item);
    },
    onSuccess: (_data, item) => {
      pendingOpenRef.current.add(item.id);
      // Remember the latest version id before this run so the auto-open waits
      // for the freshly-generated version instead of opening the previous one.
      genBaselineRef.current[item.id] = getVersions(item)[0]?._id;
      toast.success(`${item.title} generation started`);
      // Tear Sheet only: the BE flips report_status.tearsheet -> "in_progress"
      // before responding, but a fast worker can emit "completed" before the
      // refetch ever records the in_progress snapshot. The deal auto-refetch in
      // deals-layout fires only on the observed in_progress -> completed
      // transition, so without seeding it here the new version never lands in
      // aiGenerations and the row stays on "Generating…" until a manual
      // refresh. Mirrors tearsheet-v2's startMutation seed.
      if (item.id === "tear-sheet" && currentDeal.id && activeWorkspaceId) {
        queryClient.setQueryData<GetReportStatusResponse>(
          ["reportStatus", currentDeal.id, activeWorkspaceId],
          (old) =>
            old
              ? {
                  ...old,
                  opportunityApplication: {
                    ...old.opportunityApplication,
                    report_status: {
                      ...old.opportunityApplication.report_status,
                      tearsheet: "in_progress",
                    },
                  },
                }
              : old
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["reportStatus", currentDeal.id, activeWorkspaceId],
      });
    },
    onError: (error: unknown, item) => {
      // Roll back the optimistic pending flag — the run never started.
      clearPending(item.id);
      toast.error(getErrorMessage(error, `Failed to generate ${item.title}`));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({
      item,
      versionId,
    }: {
      item: ReportItem;
      versionId: string;
    }) => {
      await aiApi.deleteAiGeneration({ generationId: versionId });
      return { item, versionId };
    },
    onSuccess: ({ item, versionId }) => {
      // Tear Sheet's `dataKey` is the derived `tearsheets_all`, which the deal
      // store rebuilds from `tearsheets_v2` on every aiGenerations update — so
      // the deletion has to hit that source array, otherwise the rebuild
      // restores the just-deleted version and the row keeps showing "Open".
      const gen = currentDeal.aiGenerations as Record<string, unknown>;
      const sourceKey =
        item.dataKey === "tearsheets_all" ? "tearsheets_v2" : item.dataKey;
      const arr = Array.isArray(gen[sourceKey])
        ? (gen[sourceKey] as Array<{ _id?: string }>)
        : [];
      const next = arr.filter((r) => r._id !== versionId);
      updateDeal({
        aiGenerations: {
          ...currentDeal.aiGenerations,
          [sourceKey]: next,
        } as typeof currentDeal.aiGenerations,
      });
      // Clear any cached selection for this item (let it default to latest)
      setSelectedVersionByItem((prev) => {
        const copy = { ...prev };
        delete copy[item.id];
        return copy;
      });
      setDeleteTarget(null);
      toast.success(`${item.title} version deleted`);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete"));
      setDeleteTarget(null);
    },
  });

  // Running a premium report marks the deal premium (consumes a plan slot).
  const handleRun = (item: ReportItem) => {
    if (item.requiresActivation && currentDeal.aiTracked !== true) {
      const used = quota?.premium_deals_used;
      const cap = quota?.effective_premium_deals_cap ?? quota?.premium_deals_cap;
      if (used !== undefined && cap !== undefined) {
        // Over cap (grandfathered / post-downgrade): swap is a no-op since
        // demoting 1 still leaves us over. Block until they bring it under.
        if (used > cap) {
          toast.error(
            `Organization is over the premium cap (${used}/${cap}, shared across all workspaces). Demote at least ${used - cap + 1} premium deal${used - cap + 1 === 1 ? "" : "s"} before running new premium reports.`
          );
          return;
        }
        if (used === cap) {
          setSwapItem(item);
          return;
        }
      }
      setRunConfirmItem(item);
    } else {
      generateMutation.mutate(item);
    }
  };

  // Free up a slot for this deal, then generate the report the user clicked.
  // The swap already promoted this deal on the server, so generateMutation's
  // activateDeal call is a harmless re-activation (no extra slot consumed).
  const handleSwapConfirm = async (demoteApplicationId: string) => {
    const item = swapItem;
    if (!item || !activeWorkspaceId) return;
    setIsSwapping(true);
    try {
      await swapPremiumDeal(
        activeWorkspaceId,
        currentDeal.id,
        demoteApplicationId
      );
      queryClient.invalidateQueries({ queryKey: ["orgQuota"] });
      queryClient.invalidateQueries({ queryKey: ["orgPremiumDeals"] });
      setSwapItem(null);
      generateMutation.mutate(item);
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't free up a premium slot"));
    } finally {
      setIsSwapping(false);
    }
  };

  // Auto-open the panel when a user-triggered generation completes.
  useEffect(() => {
    const isActivated = currentDeal.aiTracked === true;
    for (const item of reportItems) {
      if (!pendingOpenRef.current.has(item.id)) continue;
      if (item.requiresActivation && !isActivated) continue;
      if (isGenerating(item)) continue;
      const versions = getVersions(item);
      if (versions.length > 0) {
        // Versioned report: open only once the just-generated version (a new
        // latest id) has arrived, and open that version explicitly so the
        // panel doesn't stay on the previous one.
        const newestId = versions[0]?._id;
        if (newestId && newestId !== genBaselineRef.current[item.id]) {
          pendingOpenRef.current.delete(item.id);
          clearPending(item.id);
          onOpenInPanel?.(item.id, item.title, newestId);
        }
      } else if (hasData(item)) {
        pendingOpenRef.current.delete(item.id);
        clearPending(item.id);
        onOpenInPanel?.(item.id, item.title);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeal.aiGenerations, currentDeal.aiTracked, reportStatus]);

  return (
    <div className="mx-auto max-w-2xl p-6">
      {currentDeal.diligenceLevel === "light" && (
        <div className="mb-4 rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm font-medium text-foreground">
            This deal had a light screen
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Market research and the website summary were generated in brief.
            Running full diligence regenerates both in depth; everything else
            on this deal stays as it is.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 gap-1.5"
            disabled={isDeepening}
            onClick={handleDeepen}
          >
            {isDeepening && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isDeepening ? "Starting…" : "Run full diligence"}
          </Button>
        </div>
      )}
      <div className="space-y-3">
        {reportItems.map((item) => {
          const completed = hasData(item);
          // Existing reports stay viewable even after a deal is demoted to
          // non-premium (SAAS-798); only generating new ones stays gated.
          const canOpen = completed;
          const generating = isGenerating(item);
          const mutating =
            generateMutation.isPending &&
            generateMutation.variables?.id === item.id;
          const pending = pendingItems.has(item.id) && !completed;
          const showSpinner = generating || mutating || pending;
          const isQueued =
            generating &&
            !mutating &&
            getReportStatusValue(item) === "queued";
          const versions = canOpen ? getVersions(item) : [];
          const selectedVersionId =
            selectedVersionByItem[item.id] || versions[0]?._id || "";

          return (
            <div
              key={item.id}
              className="rounded-lg border border-border bg-card"
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground">
                      {item.title}
                    </p>
                    {item.isPremium && (
                      <span className="text-[11px] text-muted-foreground/50">
                        Premium
                      </span>
                    )}
                  </div>
                  {canOpen && !showSpinner ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Completed
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {item.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {showSpinner && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {isQueued
                        ? "Queued…"
                        : item.id === "tear-sheet"
                          ? (tearsheetProgress(getReportStatusValue(item))?.label ?? "Generating…")
                          : "Generating…"}
                    </span>
                  )}

                  {canOpen && !showSpinner && (
                    <>
                      <VersionSelector
                        versions={versions}
                        selectedId={selectedVersionId}
                        onChange={(id) => {
                          setSelectedVersionByItem((prev) => ({
                            ...prev,
                            [item.id]: id,
                          }));
                          // Push the selection into the open panel
                          // (or open it if not yet open) so the picker
                          // feels live rather than requiring a
                          // separate "Open" click.
                          onOpenInPanel?.(item.id, item.title, id);
                        }}
                      />
                      <button
                        className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
                        onClick={() =>
                          onOpenInPanel?.(
                            item.id,
                            item.title,
                            selectedVersionId || undefined
                          )
                        }
                      >
                        Open
                      </button>
                      <ReportActionsMenu
                        disabled={deleteMutation.isPending}
                        onRegenerate={() => handleRun(item)}
                        onDelete={
                          selectedVersionId
                            ? () =>
                                setDeleteTarget({
                                  item,
                                  versionId: selectedVersionId,
                                })
                            : undefined
                        }
                      />
                    </>
                  )}

                  {!canOpen && !showSpinner && (
                    <button
                      className="rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-80 disabled:opacity-50"
                      disabled={!activeWorkspaceId}
                      onClick={() => handleRun(item)}
                    >
                      Run
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <AgentReportRows
          dealId={currentDeal.applicationId}
          onOpen={onOpenAgentReport}
        />
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.item.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this version of{" "}
              {deleteTarget?.item.title}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteMutation.mutate(deleteTarget);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PremiumActivationDialog
        open={runConfirmItem !== null}
        onClose={() => setRunConfirmItem(null)}
        onConfirm={() => {
          if (runConfirmItem) generateMutation.mutate(runConfirmItem);
          setRunConfirmItem(null);
        }}
        reportName={runConfirmItem?.title ?? ""}
        nextStage={null}
        currentTracked={quota?.premium_deals_used ?? dealStats?.tracked ?? 0}
        showStatusChange={false}
      />

      <PremiumSwapModal
        open={swapItem !== null}
        onOpenChange={(open) => {
          if (!open) setSwapItem(null);
        }}
        orgId={orgId}
        newDealName={currentDeal.overview.name}
        isSubmitting={isSwapping}
        onConfirm={handleSwapConfirm}
      />
    </div>
  );
}

export default memo(ReportsTab);
